import {Group, Stack, Text, Title} from '@mantine/core'
import type {ReactNode} from 'react'

import {outOfStateLabel} from 'src/lib/texas-bounds'
import {OutOfStateBadge} from 'src/ui/out-of-state-badge'

import type {GoogleMapsSource} from './google-maps/google-maps-metrics'
import {LocationMapPreview} from './location-map-preview'

export type LocationReadDisplay = {
    name: string
    notes?: string | null
    addressText?: string | null
    latitude: number | null
    longitude: number | null
}

type LocationReadContentProps = {
    location: LocationReadDisplay
    source: GoogleMapsSource
    layout?: 'horizontal' | 'vertical'
    mapHeight?: number
    fallbackName?: string
    showName?: boolean
    nameActions?: ReactNode
}

export function LocationReadContent({
    location,
    source,
    layout = 'horizontal',
    mapHeight = 320,
    fallbackName,
    showName = true,
    nameActions,
}: LocationReadContentProps) {
    const name = (location.name ?? '').trim() || fallbackName || 'Location'
    const notes = (location.notes ?? '').trim()
    const hasPosition = location.latitude != null && location.longitude != null

    const textContent = (
        <Stack gap="xs">
            {showName ? (
                <Group gap="xs" align="center" wrap="nowrap">
                    <Title order={4} size="h4" style={{minWidth: 0}}>
                        {name}
                    </Title>
                    <OutOfStateBadge label={outOfStateLabel(location)} />
                    {nameActions}
                </Group>
            ) : null}
            {location.addressText ? (
                <Text size="sm" c="dimmed">
                    {location.addressText}
                </Text>
            ) : null}
            {notes ? (
                <Stack gap={2}>
                    <Text size="sm" fw={700} tt="uppercase" c="dimmed">
                        Note
                    </Text>
                    <Text size="sm" fw={500} style={{whiteSpace: 'pre-wrap'}}>
                        {notes}
                    </Text>
                </Stack>
            ) : null}
            {!hasPosition ? (
                <Text size="sm" c="dimmed">
                    No map position set
                </Text>
            ) : null}
        </Stack>
    )

    const mapPreview =
        hasPosition && location.latitude != null && location.longitude != null ? (
            <LocationMapPreview
                source={source}
                latitude={location.latitude}
                longitude={location.longitude}
                height={mapHeight}
            />
        ) : null

    if (layout === 'vertical') {
        return (
            <Stack gap="sm">
                {textContent}
                {mapPreview}
            </Stack>
        )
    }

    return (
        <Group align="flex-start" wrap="nowrap" gap="md" grow>
            {textContent}
            {mapPreview}
        </Group>
    )
}
